import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { useAuth } from '../context/AuthContext';
import {
  BarChart3, PhoneCall, CalendarCheck, Clock, TrendingUp, TrendingDown,
  Euro, Percent, Activity, Minus
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

function KpiCard({ icon: Icon, label, value, suffix, change, color }) {
  const isUp = change > 0;
  const isDown = change < 0;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">
            {value}{suffix && <span className="text-base font-medium text-slate-500 ml-0.5">{suffix}</span>}
          </p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      {change !== undefined && change !== 0 && (
        <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${isUp ? 'text-emerald-600' : isDown ? 'text-red-500' : 'text-slate-400'}`}>
          {isUp ? <TrendingUp className="w-3.5 h-3.5" /> : isDown ? <TrendingDown className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
          {Math.abs(change)}% vs periodo precedente
        </div>
      )}
    </div>
  );
}

function formatDuration(seconds) {
  if (!seconds) return '0s';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

const OUTCOME_COLORS = ['#10b981', '#f59e0b', '#3b82f6', '#94a3b8'];

export default function Analytics() {
  const { token } = useAuth();
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [hours, setHours] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [ovRes, trRes, hrRes, ocRes, svRes] = await Promise.all([
        fetch(`${API}/api/analytics/overview?days=${days}`, { headers }),
        fetch(`${API}/api/analytics/calls-trend?days=${days}`, { headers }),
        fetch(`${API}/api/analytics/peak-hours?days=${days}`, { headers }),
        fetch(`${API}/api/analytics/outcomes?days=${days}`, { headers }),
        fetch(`${API}/api/analytics/top-services?days=${days}`, { headers }),
      ]);
      if (ovRes.ok) setOverview(await ovRes.json());
      if (trRes.ok) { const d = await trRes.json(); setTrend(d.trend || []); }
      if (hrRes.ok) { const d = await hrRes.json(); setHours(d.hours || []); }
      if (ocRes.ok) { const d = await ocRes.json(); setOutcomes(d.outcomes || []); }
      if (svRes.ok) { const d = await svRes.json(); setServices(d.services || []); }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [days]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
        <p className="font-medium text-slate-700 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></p>
        ))}
      </div>
    );
  };

  return (
    <Layout>
      <div className="p-4 sm:p-8 max-w-6xl" data-testid="analytics-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-sky-500" /> Analytics
            </h1>
            <p className="text-sm text-slate-500 mt-1">Performance AI e metriche chiamate</p>
          </div>
          <div className="flex gap-2" data-testid="period-selector">
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  days === d ? 'bg-sky-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`} data-testid={`period-${d}`}>
                {d === 7 ? '7 giorni' : d === 30 ? '30 giorni' : '90 giorni'}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KpiCard icon={PhoneCall} label="Chiamate AI" value={overview?.total_calls || 0}
                change={overview?.calls_change} color="bg-sky-500" />
              <KpiCard icon={Percent} label="Conversione" value={overview?.conversion_rate || 0} suffix="%"
                change={overview?.conversion_rate && overview?.prev_conversion_rate ? Math.round(overview.conversion_rate - overview.prev_conversion_rate) : undefined}
                color="bg-emerald-500" />
              <KpiCard icon={Clock} label="Durata Media" value={formatDuration(overview?.avg_duration_seconds)}
                color="bg-amber-500" />
              <KpiCard icon={Euro} label="Risparmio Stimato" value={`${overview?.estimated_savings_eur || 0}`} suffix=" EUR"
                color="bg-violet-500" />
            </div>

            {/* Secondary KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KpiCard icon={CalendarCheck} label="Prenotazioni AI" value={overview?.ai_appointments || 0}
                color="bg-teal-500" />
              <KpiCard icon={Activity} label="Minuti Totali" value={overview?.total_minutes || 0} suffix=" min"
                color="bg-rose-500" />
              <KpiCard icon={PhoneCall} label="Con Prenotazione" value={overview?.calls_with_booking || 0}
                color="bg-green-500" />
              <KpiCard icon={PhoneCall} label="Con Disdetta" value={overview?.calls_with_cancel || 0}
                color="bg-orange-500" />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              {/* Trend Chart */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5" data-testid="trend-chart">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Volume Chiamate</h3>
                {trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={trend}>
                      <defs>
                        <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.15}/>
                          <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorBookings" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.15}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                      <Area type="monotone" dataKey="calls" name="Chiamate" stroke="#0ea5e9" fill="url(#colorCalls)" strokeWidth={2} />
                      <Area type="monotone" dataKey="bookings" name="Prenotazioni" stroke="#10b981" fill="url(#colorBookings)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-sm text-slate-400 py-12">Nessun dato nel periodo</p>
                )}
              </div>

              {/* Outcomes Pie */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="outcomes-chart">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Esiti Chiamate</h3>
                {outcomes.some(o => o.value > 0) ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={outcomes} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                        paddingAngle={3} dataKey="value" nameKey="name" label={({ name, value }) => value > 0 ? `${name} (${value})` : ''}>
                        {outcomes.map((entry, i) => (
                          <Cell key={i} fill={entry.color || OUTCOME_COLORS[i]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-sm text-slate-400 py-12">Nessun dato</p>
                )}
              </div>
            </div>

            {/* Bottom Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Peak Hours */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="peak-hours-chart">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Ore di Punta</h3>
                {hours.some(h => h.calls > 0) ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={hours.filter(h => h.hour >= 7 && h.hour <= 21)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="calls" name="Chiamate" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-sm text-slate-400 py-12">Nessun dato</p>
                )}
              </div>

              {/* Top Services */}
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="top-services">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">Servizi Prenotati via AI</h3>
                {services.length > 0 ? (
                  <div className="space-y-3">
                    {services.map((s, i) => {
                      const max = services[0]?.count || 1;
                      const pct = Math.round((s.count / max) * 100);
                      return (
                        <div key={i}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="font-medium text-slate-700">{s.name}</span>
                            <span className="text-slate-500">{s.count} prenotazioni</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-sky-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-sm text-slate-400 py-12">Nessun servizio prenotato via AI</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
